import { type ReactNode, useMemo } from 'react';

import { CrmPreviewProvider, type PreviewOverlay } from '@/lib/crm/preview';
import type { ProposalResolution } from '@/lib/proposals/core/types';
import { derivePreviewOverlays } from '@/lib/proposals/utils/derivePreviewOverlays';

import { useProposalSelectionOptional } from './ProposalSelectionContext';
import { useReverseAssociationTypeResolver } from './useReverseAssociationTypeResolver';

const EMPTY_OVERLAYS: Record<string, PreviewOverlay> = {};

export function ProposalPreviewProvider({
  resolution,
  children,
}: {
  /**
   * The proposal's resolution. Only a PENDING proposal projects its actions
   * onto records: "what would this look like if approved" is the question the
   * overlay answers, and once the proposal is resolved the record is the
   * truth. Projecting on top of it can only mislead — an approved proposal
   * whose link was skipped at execution showed the skipped target as an
   * "Unknown" contact on the live deal.
   */
  resolution: ProposalResolution;
  children: ReactNode;
}): ReactNode {
  const selection = useProposalSelectionOptional();
  const actions = selection?.actions;
  // The APPLICABLE selection (checked AND valid) — a checked link whose
  // target won't exist is not projected, so an unchecked company never shows
  // up as a contact's company on its card.
  const selectedActionIds = selection?.selectedActionIds;
  const isPending = resolution === 'pending';

  const resolveReverseAssociationTypeName = useReverseAssociationTypeResolver(
    selection?.orgId
  );

  const overlays = useMemo(() => {
    // Only a PENDING proposal answers "what would this record look like if
    // approved?". Once resolved (approved / rejected / withdrawn / failed /
    // executing) the record is the truth — projecting proposal actions onto
    // it only misleads (a skipped link renders a phantom "Unknown"). #13747.
    if (!isPending || !actions || !selectedActionIds) {
      return EMPTY_OVERLAYS;
    }
    const selectedSet = new Set(selectedActionIds);
    return derivePreviewOverlays(
      actions,
      a => selectedSet.has(a.id),
      resolveReverseAssociationTypeName
    );
  }, [
    isPending,
    actions,
    selectedActionIds,
    resolveReverseAssociationTypeName,
  ]);

  if (!selection) {
    // Outside a ProposalSelectionProvider there is nothing to project:
    // CrmPreviewProvider absent -> store hooks read live data
    // (RecordDraftContext precedent).
    return children;
  }
  return (
    <CrmPreviewProvider overlays={overlays}>{children}</CrmPreviewProvider>
  );
}
